<template>
    <div>
        <div class="sm:flex sm:justify-between sm:items-center mb-8">
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-slate-800 font-bold">
                    Changelog
                </h1>
            </div>
            <div
                class="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2"
            >
                <button
                    @click="
                        showChangeLogEntryModal = true;
                        mode = 'create';
                    "
                    class="btn btn-gray text-white"
                    v-if="userSettings.is_admin"
                >
                    <svg
                        class="w-4 h-4 flex-shrink-0"
                        viewBox="0 0 16 16"
                        fill="#F27249"
                    >
                        <path
                            d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z"
                        />
                    </svg>
                    <span class="ml-2">Add Entry</span>
                </button>
            </div>
        </div>

        <div class="border-t border-slate-200">
            <div
                class="max-w-5xl mt-8 mx-auto px-4 sm:px-6 lg:px-8 md:grid gap-8"
            >
                <!-- Filters -->
                <div class="md:pl-32 md:-translate-x-16 mb-2">
                    <ul class="flex flex-wrap -m-1">
                        <li class="m-1">
                            <button
                                class="inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-transparent shadow-sm bg-orange hover:bg-orange-400 duration-150 ease-in-out"
                                @click="viewAll"
                                :class="{
                                    'bg-orange text-white hover:bg-orange-400':
                                        !activeCat,
                                    'bg-white text-slate-500 hover:bg-slate-200':
                                        activeCat,
                                }"
                            >
                                View All
                            </button>
                        </li>
                        <li
                            class="m-1"
                            v-for="(cat, cIndex) in categories"
                            :key="'category-' + cIndex"
                        >
                            <button
                                class="inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-slate-200 hover:border-slate-300 hover:bg-slate-200 shadow-sm duration-150 ease-in-out"
                                @click="setActiveCat(cat)"
                                :class="{
                                    'bg-orange text-white':
                                        activeCat === cat.id,
                                    'bg-white text-slate-500':
                                        activeCat !== cat.id,
                                }"
                            >
                                {{ cat.name }}
                            </button>
                        </li>
                    </ul>
                </div>

                <!-- Posts -->
                <div class="grid gap-8 md:-translate-x-16">
                    <!-- Post -->
                    <article
                        class="bg-white rounded-lg shadow overflow-hidden overflow-x-auto py-6 px-6 space-y-6"
                        v-for="(post, pIndex) in paginatedItems"
                        :key="'changelog-' + pIndex"
                    >
                        <div class="w-full mx-auto py-6 prose max-w-3xl">
                            <header>
                                <h2
                                    class="text-xl lg:text-2xl text-slate-800 font-bold mb-3 mt-0"
                                >
                                    <a
                                        v-if="userSettings.is_admin"
                                        href="#"
                                        @click.prevent="
                                            newPost = post;
                                            mode = 'edit';
                                            showChangeLogEntryModal = true;
                                        "
                                        role="button"
                                    >
                                        <i
                                            class="fa-light fa-edit fa-fw text-lg"
                                        ></i>
                                    </a>
                                    v{{ post.version }} - {{ post.title }}
                                </h2>
                                <div
                                    class="text-xs font-semibold uppercase text-slate-400 md:leading-8"
                                >
                                    {{ post.post_date }}
                                </div>
                                <div
                                    v-if="post.changelog_category"
                                    class="flex flex-nowrap items-center space-x-2 mb-4"
                                >
                                    <div>
                                        <div
                                            class="text-xs inline-flex font-medium bg-sky-100 text-sky-600 rounded-full text-center px-2.5 py-1"
                                        >
                                            {{ post.changelog_category.name }}
                                        </div>
                                    </div>
                                </div>
                            </header>
                            <div
                                class="space-y-3"
                                v-html="post.post_content"
                            ></div>
                        </div>
                    </article>
                </div>

                <!-- Pagination -->
                <div class="md:pl-32 md:-translate-x-16 mt-6">
                    <div
                        class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
                    >
                        <nav
                            class="mb-4 sm:mb-0 sm:order-1"
                            role="navigation"
                            aria-label="Navigation"
                        >
                            <ul class="flex justify-center">
                                <li class="ml-3 first:ml-0">
                                    <button
                                        class="btn bg-white border-slate-200 text-indigo-500"
                                        @click.prevent="prevPage"
                                        :disabled="currentPage === 0"
                                    >
                                        &lt;- Previous
                                    </button>
                                </li>
                                <li class="ml-3 first:ml-0">
                                    <button
                                        class="btn bg-white border-slate-200 hover:border-slate-300 text-indigo-500"
                                        @click.prevent="nextPage"
                                        :disabled="currentPage + 1 >= pages"
                                    >
                                        Next -&gt;
                                    </button>
                                </li>
                            </ul>
                        </nav>
                        <div
                            class="text-sm text-slate-500 text-center sm:text-left"
                        >
                            Showing
                            <span class="font-medium text-slate-600">
                                {{ startRange }}
                            </span>
                            to
                            <span class="font-medium text-slate-600">
                                {{ endRange }}
                            </span>
                            of
                            <span class="font-medium text-slate-600">
                                {{ filteredChangelogs.length }}
                            </span>
                            result{{
                                filteredChangelogs.length === 1 ? '' : 's'
                            }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal" v-if="showChangeLogEntryModal">
            <div class="modal-overlay" aria-hidden="true"></div>
            <div
                class="inner rounded-lg bg-white shadow border border-gray-200"
            >
                <a
                    href="#"
                    class="modal-close-btn text-orange"
                    role="button"
                    @click.prevent="close()"
                >
                    <i class="fal fa-times"></i>
                </a>

                <div
                    class="modal-body items-start p-8 !pb-24 border-t-4 border-solid border-t-orange flex"
                >
                    <h3
                        class="text-xl md:text-2xl text-gray-800 font-bold mb-3"
                    >
                        Changelog Entry
                    </h3>

                    <div class="grid gap-x-4 gap-y-0 md:grid-cols-3">
                        <div class="col-span-3">
                            <p class="mb-5">
                                <label
                                    for="changelog-title"
                                    class="mb-2 text-sm"
                                >
                                    Title
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    class="form-input w-full"
                                    v-model="newPost.title"
                                />
                            </p>
                        </div>
                        <div>
                            <p class="mb-5">
                                <label
                                    for="changelog-category"
                                    class="mb-2 text-sm"
                                >
                                    Category
                                </label>
                                <select
                                    v-model="newPost.changelog_category_id"
                                    id="changelog-category"
                                    class="form-select w-full"
                                >
                                    <option></option>
                                    <option
                                        v-for="(cat, cIndex) in categories"
                                        :key="'cat-' + cIndex"
                                        :value="cat.id"
                                    >
                                        {{ cat.name }}
                                    </option>
                                </select>
                            </p>
                        </div>
                        <div>
                            <p class="mb-5">
                                <label
                                    for="changelog-date"
                                    class="mb-2 text-sm"
                                >
                                    Changelog Date
                                </label>
                                <input
                                    type="date"
                                    class="w-full form-input"
                                    v-model="newPost.post_date"
                                />
                            </p>
                        </div>
                        <div>
                            <p>
                                <label
                                    for="changelog-title"
                                    class="mb-2 text-sm"
                                >
                                    Version
                                </label>
                                <input
                                    type="text"
                                    name="version"
                                    class="form-input w-full"
                                    v-model="newPost.version"
                                />
                            </p>
                        </div>
                        <div class="col-span-3">
                            <p class="mb-5">
                                <label
                                    for="changelog-content"
                                    class="mb-2 text-sm"
                                >
                                    Content
                                </label>
                                <Editor
                                    id="changelog-content"
                                    v-model="newPost.post_content"
                                    api-key="8xmbsw8rek1cr8yjd8i5f1mcqzp4gz9ym9dpa074lbh98sw0"
                                    :init="{
                                        plugins: 'lists link image table',
                                        toolbar:
                                            'undo redo | formatselect | bold italic | \
                                           alignleft aligncenter alignright alignjustify | \
                                           bullist numlist',
                                    }"
                                />
                            </p>
                        </div>
                        <div class="col-span-3 mb-8">
                            <label class="flex items-center w-56">
                                <input
                                    type="checkbox"
                                    v-model="newPost.send_to_users"
                                    value="1"
                                    class="form-checkbox"
                                />
                                <span class="ml-2 text-sm">
                                    Send Update to All Users
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
                <footer
                    class="modal-footer !flex-row flex-wrap justify-between items-center bg-gray-50"
                >
                    <div class="w-full">
                        <Errors :errors="errors" />
                    </div>
                    <button
                        type="button"
                        class="btn btn-secondary !w-48"
                        @click.prevent="close()"
                    >
                        Cancel
                    </button>
                    <a
                        href="#"
                        @click.prevent="this.confirmDelete = true"
                        role="button"
                        v-if="!confirmDelete && mode === 'edit'"
                        class="text-red-700"
                    >
                        Delete
                    </a>

                    <div v-if="confirmDelete">
                        <div
                            class="inline-flex px-2 py-2 rounded-sm text-sm bg-red-200 border border-red-200 text-red-500"
                        >
                            <div
                                class="flex w-full justify-between items-center"
                            >
                                <div class="flex items-center">
                                    <svg
                                        viewBox="0 0 512 512"
                                        class="w-5 h-5 shrink-0 fill-current opacity-80 mt-3px"
                                    >
                                        <path
                                            d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0S0 114.6 0 256S114.6 512 256 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zm32 224c0 17.7-14.3 32-32 32s-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32z"
                                        />
                                    </svg>
                                    <div class="text-sm text-left ml-3">
                                        Are you sure?
                                        <a
                                            href="#"
                                            class="text-orange font-bold hover:text-orange-light"
                                            role="button"
                                            @click.prevent="deleteChanglog"
                                        >
                                            Yes
                                        </a>
                                        or
                                        <a
                                            href="#"
                                            role="button"
                                            class="font-bold hover:text-orange-light"
                                            @click.prevent="
                                                confirmDelete = false
                                            "
                                        >
                                            No
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        v-if="mode === 'create'"
                        type="button"
                        class="btn btn-primary"
                        @click.prevent="createChangelog"
                    >
                        Save Changelog
                        <span
                            v-if="newPost.send_to_users"
                            class="font-bold text-amber-100"
                        >
                            - Sending to All Users
                        </span>
                        <span class="inline ml-2" v-if="loadingResponse">
                            <i class="fas fa-spinner-third fa-spin"></i>
                        </span>
                    </button>
                    <button
                        v-if="mode === 'edit'"
                        type="button"
                        class="btn btn-primary"
                        @click.prevent="updateChangelog"
                    >
                        Update Changelog
                        <span
                            v-if="newPost.send_to_users"
                            class="font-bold text-amber-100"
                        >
                            - Sending to All Users
                        </span>
                        <span class="inline ml-2" v-if="loadingResponse">
                            <i class="fas fa-spinner-third fa-spin"></i>
                        </span>
                    </button>
                </footer>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useAppStore } from '@/store/app';
import Editor from '@tinymce/tinymce-vue';
import Errors from '@/components/errors.vue';

export default {
    components: {
        Editor,
        Errors,
    },
    computed: {
        ...mapState(useAppStore, ['userSettings']),
        filteredChangelogs() {
            return this.changelogs.filter((cl) => {
                if (!this.activeCat) {
                    return true;
                }
                return cl.changelog_category_id === this.activeCat;
            });
        },
        paginatedItems() {
            const start = this.currentPage * this.perPage,
                end = start + this.perPage;

            return this.filteredChangelogs.slice(start, end);
        },
        pages() {
            return Math.ceil(this.filteredChangelogs.length / this.perPage);
        },
        startRange() {
            const start = (this.currentPage + 1) * this.perPage - this.perPage;
            return start ? start : 1;
        },
        endRange() {
            let endRange = (this.currentPage + 1) * this.perPage;
            if (endRange > this.filteredChangelogs.length) {
                endRange = this.filteredChangelogs.length;
            }

            return endRange;
        },
    },
    data: () => ({
        errors: [],
        categories: [],
        changelogs: [],
        perPage: 10,
        currentPage: 0,
        showChangeLogEntryModal: false,
        loadingResponse: false,
        activeCat: 0,
        mode: 'create',
        newPost: {
            id: '',
            title: '',
            post_content: '',
            changelog_category_id: '',
            post_date: '',
            version: '',
            send_to_users: false,
        },
        confirmDelete: false,
    }),
    methods: {
        getChangelogs() {
            ApiService.apiCall('/changelogs', 'get')
                .then((response) => {
                    this.changelogs = response.data.changelogs;
                    this.categories = response.data.categories;
                })
                .catch((response) => {});
        },
        createChangelog() {
            this.loadingResponse = true;
            this.validateForm();

            if (this.errors.length === 0) {
                ApiService.apiCall('/admin/changelogs', 'post', this.newPost)
                    .then((response) => {
                        this.getChangelogs();
                        this.resetData();
                        this.loadingResponse = false;
                        this.showChangeLogEntryModal = false;
                        this.confirmDelete = false;
                        const sentToUsers = response.data.sent_to_users;

                        this.$notify(
                            {
                                group: 'success',
                                title: 'Success',
                                text:
                                    'Changelog added.' +
                                    (sentToUsers ? ' Sent to all users.' : ''),
                            },
                            6000
                        );
                    })
                    .catch((e) => {
                        console.log(e.message);
                        this.loadingResponse = false;
                    });
            }
        },
        updateChangelog() {
            this.loadingResponse = true;
            this.validateForm();

            ApiService.apiCall(
                '/admin/changelogs/' + this.newPost.id,
                'put',
                this.newPost
            )
                .then((response) => {
                    this.getChangelogs();
                    this.resetData();
                    this.loadingResponse = false;
                    this.showChangeLogEntryModal = false;
                    this.confirmDelete = false;
                    const sentToUsers = response.data.sent_to_users;

                    this.$notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text:
                                'Changelog updated.' +
                                (sentToUsers ? ' Sent to all users.' : ''),
                        },
                        6000
                    );
                })
                .catch((response) => {
                    this.loadingResponse = false;
                });
        },
        validateForm() {
            this.errors = [];
            if (this.newPost.title === '') {
                this.errors.push({ message: 'Please enter the title' });
            }

            if (this.newPost.post_content === '') {
                this.errors.push({ message: 'Please enter the content' });
            }

            if (this.newPost.changelog_category_id === '') {
                this.errors.push({ message: 'Please choose the category' });
            }

            if (this.newPost.post_date === '') {
                this.errors.push({ message: 'Please set the post date' });
            }

            if (this.newPost.version === '') {
                this.errors.push({ message: 'Please set the version' });
            }

            if (this.errors.length > 0) {
                this.loadingResponse = false;
            }
        },
        deleteChanglog() {
            ApiService.apiCall('/admin/changelogs/' + this.newPost.id, 'delete')
                .then(() => {
                    this.getChangelogs();
                    this.resetData();
                    this.showChangeLogEntryModal = false;
                    this.confirmDelete = false;
                    this.$notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text: 'Changelog deleted',
                        },
                        4000
                    );
                })
                .catch((response) => {
                    this.loadingResponse = false;
                });
        },
        resetData() {
            this.newPost = {
                title: '',
                post_content: '',
                changelog_category_id: '',
                post_date: '',
                version: '',
                send_to_users: false,
            };
        },
        viewAll() {
            this.activeCat = 0;
        },
        setActiveCat(cat) {
            this.activeCat = cat.id;
        },
        nextPage() {
            this.currentPage++;
        },
        prevPage() {
            this.currentPage--;
        },
        close() {
            this.resetData();
            this.showChangeLogEntryModal = false;
        },
    },
    created() {
        this.getChangelogs();
    },
    mounted() {},
};
</script>
